import React, { useState } from 'react'
import { rubricActions } from '../../../../../store/slices/rubric.slice'
import { Alert, Button, Flex, Space } from 'antd'
import { EditOutlined, PlusOutlined } from '@ant-design/icons'
import { useDispatch } from 'react-redux'
import PatternIcon from '../../../../../components/common/PatternIcon'
import Title from 'antd/es/typography/Title'
import SortableList from '../../../../../components/common/sortable-list/SortableList'
import { DragHandle, SortableItem } from '../../../../../components/common/sortable-list/SortableItem'
import { useReorderRubricCategoriesMutation } from '../../../../../store/apis/rubric.api'
import { openNotificationWithIcon } from '../../../../../utils/notification.helper'
import { useAssignmentAccess } from '../../../components/access/Access'
import { Perm, State } from '../../../../../constants/assignment-permissions'

const CategoriesList = ({ categories = [], rubricId }) => {
    const dispatch = useDispatch()

    const [reorderCategories, reorderCategoriesResult] = useReorderRubricCategoriesMutation()
    const [reorderedCategories, setReorderedCategories] = useState(null)

    const { hasAccess: hasEditPermission } = useAssignmentAccess([Perm.CAN_EDIT, State.NOT_PROCESSING, State.NOT_MBA, State.UNPUBLISHED, State.LATEST])

    const handleSaveReorderedCategories = async () => {
        const idToOrderMap = {}
        reorderedCategories.forEach((data, index) => (idToOrderMap[data.id] = index))
        const response = await reorderCategories({ rubricId, idToOrderMap })
        if ('data' in response) {
            setReorderedCategories(null)
            openNotificationWithIcon('success', 'Categories order saved successfully')
        }
    }

    const header = (
        <Flex vertical gap={8}>
            <Flex justify='space-between'>
                <Title level={4} style={{ margin: 0, fontSize: '1rem' }}>
                    Categories
                </Title>
                <Flex gap={8}>
                    {reorderedCategories ? (
                        <>
                            <Button
                                onClick={() => setReorderedCategories(null)}
                                danger
                                disabled={reorderCategoriesResult.isLoading}
                            >
                                Revert
                            </Button>
                            <Button
                                type='primary'
                                onClick={handleSaveReorderedCategories}
                                loading={reorderCategoriesResult.isLoading}
                            >
                                Save order
                            </Button>
                        </>
                    ) : (
                        hasEditPermission && (
                            <Button
                                icon={<PlusOutlined aria-hidden='true' />}
                                onClick={() => dispatch(rubricActions.setShowCreateCategoryDrawer(true))}
                                type='text'
                            >
                                Create New
                            </Button>
                        )
                    )}
                </Flex>
            </Flex>
            {reorderCategoriesResult.isError && (
                <Alert message={reorderCategoriesResult?.error} type='error' showIcon closable />
            )}
        </Flex>
    )

    const handleChange = newItems => {
        if (hasEditPermission) setReorderedCategories(newItems)
    }

    return (
        <SortableList
            items={reorderedCategories || categories}
            header={header}
            onChange={handleChange}
            renderItem={(category, index) => {
                return (
                    <SortableItem id={String(category.id)} key={category.id} index={index}>
                        <Flex justify='space-between' className='f-width'>
                            <Space size={4}>
                                {hasEditPermission && <DragHandle tooltipText='Move category' />}
                                <PatternIcon
                                    id={category.id}
                                    colorId={category.colorId}
                                    content={category.grade}
                                    suffix='pt'
                                />{' '}
                                {category.name}
                            </Space>
                            <Button
                                shape='circle'
                                type='text'
                                aria-label='Edit'
                                title='Edit'
                                onClick={() => dispatch(rubricActions.setEditingCategoryId(category.id))}
                            >
                                <EditOutlined className='primary-color' />
                            </Button>
                        </Flex>
                    </SortableItem>
                )
            }}
        />
    )
}

export default CategoriesList
